import { useState, useEffect, useCallback } from 'react';
import {
  MapPin,
  Target,
  TrendingUp,
  Grid3x3,
  BarChart3,
  RefreshCw,
  Loader2,
  ArrowUp,
  ArrowDown,
  Minus,
  Star,
  ChevronDown,
  Play
} from 'lucide-react';
import api, { LocalPackRankData, GeoGridSnapshot } from '../services/api';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useClient } from '../contexts/ClientContext';

type TabType = 'rankings' | 'geogrid';

const getPositionColor = (position: number | null): string => {
  if (position === null) return 'text-gray-500 dark:text-gray-500';
  if (position <= 3) return 'text-green-500';
  if (position <= 7) return 'text-yellow-500';
  if (position <= 15) return 'text-orange-500';
  return 'text-red-500';
};

const getPositionBg = (position: number | null): string => {
  if (position === null) return 'bg-gray-600/20 border-gray-600';
  if (position <= 3) return 'bg-green-500/20 border-green-500';
  if (position <= 7) return 'bg-yellow-500/20 border-yellow-500';
  if (position <= 15) return 'bg-orange-500/20 border-orange-500';
  return 'bg-red-500/20 border-red-500';
};

const getPositionLabel = (position: number | null): string => {
  if (position === null) return 'NR';
  return `#${position}`;
};

const LocalRankTracker = () => {
  const { selectedClientId } = useClient();
  const [activeTab, setActiveTab] = useState<TabType>('rankings');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Rankings state
  const [rankData, setRankData] = useState<LocalPackRankData | null>(null);
  const [selectedCity, setSelectedCity] = useState<string>('all');
  const [dateRange, setDateRange] = useState<number>(30);
  const [expandedKeyword, setExpandedKeyword] = useState<string | null>(null);

  // Geo Grid state
  const [grids, setGrids] = useState<GeoGridSnapshot[]>([]);
  const [selectedGrid, setSelectedGrid] = useState<GeoGridSnapshot | null>(null);
  const [gridCity, setGridCity] = useState<string>('');
  const [gridKeyword, setGridKeyword] = useState<string>('');
  const [gridsLoading, setGridsLoading] = useState(false);
  const [runningGrid, setRunningGrid] = useState(false);

  // Load rankings data
  const loadRankings = useCallback(async () => {
    if (!selectedClientId) return;
    try {
      setLoading(true);
      setError(null);
      const params: { days?: number; city?: string } = { days: dateRange };
      if (selectedCity !== 'all') params.city = selectedCity;
      const data = await api.getLocalPackRankings(selectedClientId, params);
      setRankData(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load local pack rankings');
    } finally {
      setLoading(false);
    }
  }, [selectedClientId, dateRange, selectedCity]);

  // Load geo grids
  const loadGeoGrids = useCallback(async () => {
    if (!selectedClientId) return;
    try {
      setGridsLoading(true);
      const params: { city?: string } = {};
      if (gridCity) params.city = gridCity;
      const data = await api.getGeoGrids(selectedClientId, params);
      setGrids(data);
      if (data.length > 0 && !selectedGrid) {
        setSelectedGrid(data[0]);
      }
    } catch (err) {
      console.error('Failed to load geo grids:', err);
    } finally {
      setGridsLoading(false);
    }
  }, [selectedClientId, gridCity, selectedGrid]);

  useEffect(() => {
    loadRankings();
  }, [loadRankings]);

  useEffect(() => {
    if (activeTab === 'geogrid') {
      loadGeoGrids();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedClientId]);

  const handleRunGeoGrid = async () => {
    if (!selectedClientId || !gridCity || !gridKeyword) return;
    try {
      setRunningGrid(true);
      await api.runGeoGrid(selectedClientId, gridCity, gridKeyword);
      // Reload grids after a short delay to allow processing
      setTimeout(() => {
        loadGeoGrids();
        setRunningGrid(false);
      }, 2000);
    } catch (err) {
      console.error('Failed to run geo grid:', err);
      setError('Failed to run geo grid scan');
      setRunningGrid(false);
    }
  };

  const handleSelectGrid = async (grid: GeoGridSnapshot) => {
    try {
      const detail = await api.getGeoGrid(grid.id);
      setSelectedGrid(detail);
    } catch (err) {
      console.error('Failed to load grid detail:', err);
    }
  };

  const cities = rankData ? Object.keys(rankData.cities) : [];

  // Build the grid visualization from results
  const buildGridMatrix = (grid: GeoGridSnapshot) => {
    const size = grid.grid_size || 3;
    const matrix: (typeof grid.results[0] | null)[][] = Array.from({ length: size }, () =>
      Array.from({ length: size }, () => null)
    );
    grid.results.forEach(r => {
      if (r.grid_row < size && r.grid_col < size) {
        matrix[r.grid_row][r.grid_col] = r;
      }
    });
    return matrix;
  };

  if (loading && activeTab === 'rankings') {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-black flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-black">
      {/* Header */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-3 py-2">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-base font-bold text-gray-900 dark:text-white">Local Pack Rank Tracker</h1>
              <p className="text-gray-600 dark:text-gray-300">
                Google Maps rankings & geo-grid visibility
              </p>
            </div>
            <div className="flex items-center gap-3">
              {activeTab === 'rankings' && (
                <>
                  {/* City Filter */}
                  <select
                    value={selectedCity}
                    onChange={(e) => setSelectedCity(e.target.value)}
                    className="bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 min-w-[180px]"
                  >
                    <option value="all">All Cities</option>
                    {cities.map((city) => (
                      <option key={city} value={city}>{city}</option>
                    ))}
                  </select>

                  {/* Date Range */}
                  <select
                    value={dateRange}
                    onChange={(e) => setDateRange(Number(e.target.value))}
                    className="bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600"
                  >
                    <option value={7}>Last 7 days</option>
                    <option value={14}>Last 14 days</option>
                    <option value={30}>Last 30 days</option>
                    <option value={60}>Last 60 days</option>
                    <option value={90}>Last 90 days</option>
                  </select>
                </>
              )}

              <button
                onClick={activeTab === 'rankings' ? loadRankings : loadGeoGrids}
                disabled={loading || gridsLoading}
                className="flex items-center gap-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
              >
                <RefreshCw className={`w-5 h-5 ${(loading || gridsLoading) ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 py-3">
        {/* Error Message */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-2 flex items-center justify-between">
            <span>{error}</span>
            <button
              onClick={() => setError(null)}
              className="text-sm text-red-700 hover:text-red-900 underline"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-2">
          {(['rankings', 'geogrid'] as TabType[]).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                activeTab === tab
                  ? 'bg-primary-500 text-black'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              {tab === 'rankings' ? (
                <>
                  <BarChart3 className="w-4 h-4" />
                  Rankings
                </>
              ) : (
                <>
                  <Grid3x3 className="w-4 h-4" />
                  Geo Grid
                </>
              )}
            </button>
          ))}
        </div>

        {/* Rankings Tab */}
        {activeTab === 'rankings' && rankData && (
          <div className="space-y-3">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <div className="bg-white dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                    <Target className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-900 dark:text-white">
                      {rankData.summary.total_keywords_tracked}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300">Keywords Tracked</p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
                    <TrendingUp className="w-6 h-6 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-900 dark:text-white">
                      {rankData.summary.total_ranking}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300">Currently Ranking</p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                    <BarChart3 className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-900 dark:text-white">
                      {rankData.summary.avg_position !== null ? rankData.summary.avg_position.toFixed(1) : 'N/A'}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300">Avg Position</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Rankings by City */}
            {Object.entries(rankData.cities).map(([cityName, cityData]) => (
              <div key={cityName} className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-primary-500" />
                  <h2 className="text-sm font-semibold text-gray-900 dark:text-white">{cityName}</h2>
                  <span className="text-xs text-gray-500 dark:text-gray-400 ml-auto">
                    {Object.keys(cityData.keywords).length} keywords
                  </span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Keyword</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Position</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Previous</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Change</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Trend</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {Object.entries(cityData.keywords).map(([keyword, kwData]) => {
                        const isExpanded = expandedKeyword === `${cityName}-${keyword}`;
                        return (
                          <tr
                            key={keyword}
                            className="hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer"
                            onClick={() => setExpandedKeyword(isExpanded ? null : `${cityName}-${keyword}`)}
                          >
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <ChevronDown className={`w-3 h-3 text-gray-400 transition-transform ${isExpanded ? '' : '-rotate-90'}`} />
                                <span className="font-medium text-gray-900 dark:text-white">{keyword}</span>
                              </div>
                              {/* Expanded: show rank history chart */}
                              {isExpanded && kwData.history && kwData.history.length > 1 && (
                                <div className="mt-3 ml-5" onClick={(e) => e.stopPropagation()}>
                                  <div className="h-32 w-full max-w-md">
                                    <ResponsiveContainer width="100%" height="100%">
                                      <LineChart data={kwData.history.map(h => ({
                                        date: new Date(h.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                                        position: h.position
                                      }))}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                                        <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#9CA3AF' }} />
                                        <YAxis reversed domain={[1, 'auto']} tick={{ fontSize: 10, fill: '#9CA3AF' }} />
                                        <Tooltip
                                          contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                                          labelStyle={{ color: '#F9FAFB' }}
                                          itemStyle={{ color: '#34D399' }}
                                        />
                                        <Line type="monotone" dataKey="position" stroke="#34D399" strokeWidth={2} dot={{ r: 3 }} />
                                      </LineChart>
                                    </ResponsiveContainer>
                                  </div>
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className={`inline-flex items-center justify-center w-10 h-7 rounded border text-sm font-bold ${getPositionBg(kwData.current_position)} ${getPositionColor(kwData.current_position)}`}>
                                {getPositionLabel(kwData.current_position)}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className="text-sm text-gray-500 dark:text-gray-400">
                                {kwData.previous_position !== null ? `#${kwData.previous_position}` : '-'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              {kwData.change !== 0 ? (
                                <span className={`inline-flex items-center gap-1 text-sm font-medium ${
                                  kwData.change > 0 ? 'text-green-500' : 'text-red-500'
                                }`}>
                                  {kwData.change > 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                                  {Math.abs(kwData.change)}
                                </span>
                              ) : (
                                <Minus className="w-4 h-4 text-gray-400 mx-auto" />
                              )}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {kwData.history && kwData.history.length > 1 ? (
                                <div className="inline-flex items-center gap-0.5">
                                  {kwData.history.slice(-7).map((h, i) => (
                                    <div
                                      key={i}
                                      className={`w-1.5 rounded-full ${
                                        h.position === null ? 'bg-gray-600 h-1' :
                                        h.position <= 3 ? 'bg-green-500' :
                                        h.position <= 7 ? 'bg-yellow-500' :
                                        h.position <= 15 ? 'bg-orange-500' : 'bg-red-500'
                                      }`}
                                      style={{ height: h.position === null ? '4px' : `${Math.max(4, 20 - (h.position * 0.8))}px` }}
                                      title={`${h.date}: ${h.position !== null ? `#${h.position}` : 'NR'}`}
                                    />
                                  ))}
                                </div>
                              ) : (
                                <span className="text-xs text-gray-500">-</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}

            {/* Empty State */}
            {Object.keys(rankData.cities).length === 0 && (
              <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-8 text-center">
                <Target className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  No Rankings Data Yet
                </h3>
                <p className="text-gray-500 dark:text-gray-400">
                  Local pack rank tracking data will appear here once the system starts collecting data for this client.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Geo Grid Tab */}
        {activeTab === 'geogrid' && (
          <div className="space-y-3">
            {/* Run New Grid */}
            <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Run New Geo Grid Scan</h2>
              <div className="flex flex-col sm:flex-row items-start sm:items-end gap-3">
                <div className="flex-1 w-full sm:w-auto">
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">City</label>
                  <input
                    type="text"
                    value={gridCity}
                    onChange={(e) => setGridCity(e.target.value)}
                    placeholder="e.g. Houston, TX"
                    className="w-full bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600"
                  />
                </div>
                <div className="flex-1 w-full sm:w-auto">
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Keyword</label>
                  <input
                    type="text"
                    value={gridKeyword}
                    onChange={(e) => setGridKeyword(e.target.value)}
                    placeholder="e.g. plumber near me"
                    className="w-full bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600"
                  />
                </div>
                <button
                  onClick={handleRunGeoGrid}
                  disabled={runningGrid || !gridCity || !gridKeyword}
                  className="flex items-center gap-2 bg-primary-500 text-black px-4 py-2 rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 whitespace-nowrap"
                >
                  {runningGrid ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Play className="w-4 h-4" />
                  )}
                  Run Grid Scan
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
              {/* Grid Visualization */}
              <div className="lg:col-span-2">
                {selectedGrid ? (
                  <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                      <div className="flex items-center justify-between">
                        <div>
                          <h2 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                            <Grid3x3 className="w-4 h-4 text-primary-500" />
                            {selectedGrid.keyword}
                          </h2>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {new Date(selectedGrid.created_at).toLocaleDateString('en-US', {
                              month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
                            })}
                          </p>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="text-xs text-gray-500 dark:text-gray-400">Visibility</p>
                            <p className={`text-sm font-bold ${
                              (selectedGrid.visibility_pct || 0) >= 70 ? 'text-green-500' :
                              (selectedGrid.visibility_pct || 0) >= 40 ? 'text-yellow-500' : 'text-red-500'
                            }`}>
                              {selectedGrid.visibility_pct !== null ? `${selectedGrid.visibility_pct.toFixed(0)}%` : 'N/A'}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-gray-500 dark:text-gray-400">Avg Pos</p>
                            <p className={`text-sm font-bold ${getPositionColor(selectedGrid.avg_position)}`}>
                              {selectedGrid.avg_position !== null ? selectedGrid.avg_position.toFixed(1) : 'N/A'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="p-4">
                      {/* Grid Matrix */}
                      <div className="flex justify-center">
                        <div
                          className="grid gap-2"
                          style={{
                            gridTemplateColumns: `repeat(${selectedGrid.grid_size || 3}, minmax(0, 1fr))`,
                            maxWidth: `${(selectedGrid.grid_size || 3) * 140}px`
                          }}
                        >
                          {buildGridMatrix(selectedGrid).map((row, rowIdx) =>
                            row.map((cell, colIdx) => (
                              <div
                                key={`${rowIdx}-${colIdx}`}
                                className={`rounded-lg border p-3 min-w-[120px] ${
                                  cell ? getPositionBg(cell.local_pack_position) : 'bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-600'
                                }`}
                              >
                                {cell ? (
                                  <div>
                                    <div className="flex items-center justify-between mb-1">
                                      <span className={`text-lg font-bold ${getPositionColor(cell.local_pack_position)}`}>
                                        {getPositionLabel(cell.local_pack_position)}
                                      </span>
                                      {cell.local_pack_rating && (
                                        <span className="flex items-center gap-0.5 text-xs text-yellow-500">
                                          <Star className="w-3 h-3 fill-yellow-500" />
                                          {cell.local_pack_rating}
                                        </span>
                                      )}
                                    </div>
                                    <p className="text-xs text-gray-700 dark:text-gray-300 truncate" title={cell.location_name}>
                                      {cell.location_name}
                                    </p>
                                    {cell.local_pack_title && (
                                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5" title={cell.local_pack_title}>
                                        {cell.local_pack_title}
                                      </p>
                                    )}
                                    {cell.local_pack_reviews !== null && cell.local_pack_reviews !== undefined && (
                                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                        {cell.local_pack_reviews} reviews
                                      </p>
                                    )}
                                  </div>
                                ) : (
                                  <div className="text-center text-gray-400 text-xs py-2">
                                    No data
                                  </div>
                                )}
                              </div>
                            ))
                          )}
                        </div>
                      </div>

                      {/* Legend */}
                      <div className="flex items-center justify-center gap-4 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <div className="flex items-center gap-1.5">
                          <div className="w-3 h-3 rounded bg-green-500/30 border border-green-500" />
                          <span className="text-xs text-gray-500 dark:text-gray-400">#1-3</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <div className="w-3 h-3 rounded bg-yellow-500/30 border border-yellow-500" />
                          <span className="text-xs text-gray-500 dark:text-gray-400">#4-7</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <div className="w-3 h-3 rounded bg-orange-500/30 border border-orange-500" />
                          <span className="text-xs text-gray-500 dark:text-gray-400">#8-15</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <div className="w-3 h-3 rounded bg-red-500/30 border border-red-500" />
                          <span className="text-xs text-gray-500 dark:text-gray-400">#16+</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <div className="w-3 h-3 rounded bg-gray-600/30 border border-gray-600" />
                          <span className="text-xs text-gray-500 dark:text-gray-400">NR</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-8 text-center">
                    {gridsLoading ? (
                      <Loader2 className="w-6 h-6 animate-spin mx-auto text-primary-500" />
                    ) : (
                      <>
                        <Grid3x3 className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                          No Geo Grid Data
                        </h3>
                        <p className="text-gray-500 dark:text-gray-400">
                          Run a geo grid scan above to visualize local pack rankings across a geographic area.
                        </p>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Grid Snapshots List */}
              <div className="lg:col-span-1">
                <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
                  <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                    <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Previous Scans</h2>
                  </div>
                  <div className="p-2 max-h-[600px] overflow-y-auto">
                    {gridsLoading ? (
                      <div className="py-8 text-center">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto text-primary-500" />
                      </div>
                    ) : grids.length > 0 ? (
                      <div className="space-y-1">
                        {grids.map((grid) => (
                          <button
                            key={grid.id}
                            onClick={() => handleSelectGrid(grid)}
                            className={`w-full text-left p-3 rounded-lg transition-colors ${
                              selectedGrid?.id === grid.id
                                ? 'bg-primary-500/10 border border-primary-500/30'
                                : 'hover:bg-gray-50 dark:hover:bg-gray-800 border border-transparent'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                {grid.keyword}
                              </span>
                              {grid.visibility_pct !== null && (
                                <span className={`text-xs font-bold ml-2 ${
                                  grid.visibility_pct >= 70 ? 'text-green-500' :
                                  grid.visibility_pct >= 40 ? 'text-yellow-500' : 'text-red-500'
                                }`}>
                                  {grid.visibility_pct.toFixed(0)}%
                                </span>
                              )}
                            </div>
                            <div className="flex items-center justify-between mt-1">
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {new Date(grid.created_at).toLocaleDateString('en-US', {
                                  month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                                })}
                              </span>
                              {grid.avg_position !== null && (
                                <span className={`text-xs ${getPositionColor(grid.avg_position)}`}>
                                  Avg: {grid.avg_position.toFixed(1)}
                                </span>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-6">
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          No previous scans found.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LocalRankTracker;
